import React, { useState } from 'react';
import ArrayBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/ArrayBuilder';
import { Icon, SelectMenu, Typography } from '@scale/scaleui';
import { StringThresholdTypeSchemaMap } from '@scale/shared/pipelinev3/genai';
import {
  StringThreshold,
  StringRoutingTypeOverrideType,
  StringValueComparator,
} from '@scale/shared-types';
import type { TypeBuilderMapType } from 'react-scale/corp/components/QM/taskTemplatesEditor/BuilderSchemasType';

interface StringRoutingThresholdsBuilderProps {
  name: string;
  existingValue?: Array<StringThreshold>;
  updateProperty: (arg: Array<StringThreshold> | undefined) => void;
  required?: boolean;
  typeBuilderMap: TypeBuilderMapType;
}

const stepTypeOptions = [
  {
    id: StringRoutingTypeOverrideType.ModelResponseSelector,
    name: 'Model Response Selector Step Type',
  },
  {
    id: StringRoutingTypeOverrideType.TextCollection,
    name: 'Text Collection Step Type',
  },
  {
    id: StringRoutingTypeOverrideType.ResponseTextCollection,
    name: 'Response Text Collection Step Type',
  },
  {
    id: StringRoutingTypeOverrideType.TextCollectionPerResponse,
    name: 'Text Collection Per Response Step Type',
  },
  {
    id: StringRoutingTypeOverrideType.QualityMeasurement,
    name: 'Quality Measurement Step Type',
  },
  {
    id: StringRoutingTypeOverrideType.NamedEntityRecognition,
    name: 'Named Entity Recognition Step Type',
  },
];

const StringRoutingThresholdsBuilder: React.FC<StringRoutingThresholdsBuilderProps> = ({
  name,
  existingValue = [],
  updateProperty,
  typeBuilderMap,
}) => {
  const thresholds = existingValue;
  const [selectedStepType, setSelectedStepType] = useState<StringRoutingTypeOverrideType>(
    StringRoutingTypeOverrideType.ModelResponseSelector,
  );

  const addThreshold = (stepType: StringRoutingTypeOverrideType) => {
    const newThresholds = [...thresholds];
    newThresholds.push({
      stepType,
      value: '',
      comparator: StringValueComparator.Equals,
      ...(stepType === StringRoutingTypeOverrideType.ModelResponseSelector && {
        checkIsObjective: false,
        isObjective: false,
      }),
      ...(stepType === StringRoutingTypeOverrideType.TextCollectionPerResponse && {
        modelId: '',
      }),
      ...((stepType === StringRoutingTypeOverrideType.TextCollection ||
        stepType === StringRoutingTypeOverrideType.TextCollectionPerResponse ||
        stepType === StringRoutingTypeOverrideType.ResponseTextCollection) && { fieldId: '' }),
    } as any);

    updateProperty(newThresholds);
  };

  return (
    <div className="scaleui scaleui-tailwind scale-theme">
      {name && <div className="my-2 text-sm font-medium leading-6 text-neutral-700">{name}</div>}
      <ArrayBuilder
        typeBuilderMap={typeBuilderMap}
        elements={thresholds}
        setElements={updateProperty}
        getKey={threshold => JSON.stringify(threshold)}
        getTitle={threshold => `${threshold.stepType}`}
        getSchema={threshold => StringThresholdTypeSchemaMap[threshold.stepType]}
        delimiter={<Typography.Subtitle4>AND</Typography.Subtitle4>}
      />
      <div className="flex">
        <SelectMenu
          className="min-w-[135px]"
          options={stepTypeOptions}
          selectedOptionId={selectedStepType}
          setSelected={value => {
            setSelectedStepType(value as StringRoutingTypeOverrideType);
          }}
        />
        <Icon
          className="ml-4"
          hasBackground
          icon={['fas', 'plus']}
          onClick={() => addThreshold(selectedStepType)}
          size="lg"
          variant="primary"
        />
      </div>
    </div>
  );
};

export default StringRoutingThresholdsBuilder;
